import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useLocation } from 'react-router-dom';
import TopNav from '../components/feature/TopNav';
import { loadAdvisorEssentialData } from '../services/advisorApiAdapter';
import { mvpApi } from '../services/api';
import FamilyHeaderStats from './family-view/components/FamilyHeaderStats';
import FamilyMembersTable from './family-view/components/FamilyMembersTable';
import CaseHistoryTable from './family-view/components/CaseHistoryTable';
import { SkeletonCaseDetail } from '../components/advisor/SkeletonLoader';

export default function AdvisorFamilyView() {
  const { t } = useTranslation();
  const { familyGroupId } = useParams();
  const location = useLocation();
  const [data, setData] = React.useState({ users: [], cases: [] });
  const [advisorName, setAdvisorName] = React.useState(() => location.state?.advisorName || 'Advisor');
  const [familyName, setFamilyName] = React.useState(() => location.state?.familyName || familyGroupId || t('advisorPortal.defaultFamilyName'));
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadAdvisorEssentialData().then(({ cases, me }) => {
      setData((prev) => ({ ...prev, cases }));
      if (!location.state?.advisorName) {
        const name = `${me?.first_name || ''} ${me?.last_name || ''}`.trim() || me?.name || me?.email;
        if (name) setAdvisorName(name);
      }
    });
  }, [location.state?.advisorName]);

  React.useEffect(() => {
    if (!familyGroupId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    mvpApi
      .getFamilyMembers(familyGroupId)
      .then((res) => {
        const payload = res?.data || {};
        setFamilyName(payload.family_name || familyGroupId);
        const mapped = (payload.members || []).map((m) => ({
          id: m.user_id,
          user_id: m.user_id,
          name: m.name,
          email: m.email,
          phone: m.phone,
          relationship_to_lead: m.relationship_to_lead,
          is_lead_contact: m.is_lead_contact,
        }));
        setMembers(mapped);
      })
      .catch(() => {
        setFamilyName(familyGroupId);
        setMembers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [familyGroupId]);

  const familyCaseIds = React.useMemo(() => new Set(members.map((m) => String(m.id))), [members]);
  const familyCases = React.useMemo(() => data.cases.filter((c) => familyCaseIds.has(String(c.user_id))), [data.cases, familyCaseIds]);

  if (loading) {
    return <SkeletonCaseDetail />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav advisorName={advisorName} />
      <main className="flex-1 px-8 py-8 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <Link to="/advisor" className="hover:text-slate-700 transition-colors cursor-pointer">{t('advisorPortal.familyApplications')}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-slate-800 font-semibold">
            {familyName || t('advisorPortal.defaultFamilyName')} {t('advisorPortal.familySuffixTitle')}
          </span>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {familyName || t('advisorPortal.defaultFamilyName')} {t('advisorPortal.familySuffixTitle')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t('advisorPortal.familyOverview')}</p>
          </div>
          <Link to="/advisor" className="flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 bg-white px-3.5 py-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line text-sm" /> {t('advisorPortal.familyBackToSearch')}
          </Link>
        </div>

        <div className="mb-6">
          <FamilyHeaderStats members={members} />
        </div>
        <div className="mb-6">
          <FamilyMembersTable members={members} advisorName={advisorName} />
        </div>
        <div>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-history-line text-slate-500 text-base" />
            </div>
            <h2 className="text-sm font-semibold text-slate-800">{t('advisorPortal.familyCaseHistory')}</h2>
          </div>
          <CaseHistoryTable members={members} cases={familyCases} />
        </div>
      </main>
    </div>
  );
}

